import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const Toast = ({ id, message, type, onClose, shouldBounce }) => {
  // Icon SVGs
  const icon = type === 'success' ? (
    <svg className="h-6 w-6 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="12" fill="#22c55e" opacity="0.15" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
    </svg>
  ) : (
    <svg className="h-6 w-6 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="12" fill="#ef4444" opacity="0.15" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
    </svg>
  );

  // Bounce animation: force reflow to retrigger animation on identical toasts
  const toastRef = useRef();
  useEffect(() => {
    if (shouldBounce && toastRef.current) {
      toastRef.current.classList.remove('animate-toast-bounce');
      void toastRef.current.offsetWidth;
      toastRef.current.classList.add('animate-toast-bounce');
    }
  }, [message, type, id, shouldBounce]);

  return createPortal(
    <div
      ref={toastRef}
      className={
        `fixed bottom-4 right-4 z-50 min-w-[320px] max-w-xs flex items-center px-4 py-3 rounded-lg border border-gray-700 shadow-lg bg-gray-900 text-white animate-fade-in ${shouldBounce ? 'animate-toast-bounce' : ''}`
      }
      role="alert"
      aria-live="assertive"
    >
      <div className="mr-3 flex items-center">{icon}</div>
      <div className="flex-1 text-base font-medium">{message}</div>
      <button
        onClick={onClose}
        className="ml-4 text-gray-400 hover:text-white focus:outline-none"
        aria-label="Dismiss notification"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10l-4.95-4.95A1 1 0 115.05 3.636L10 8.586z" clipRule="evenodd" />
        </svg>
      </button>
    </div>,
    document.body
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const lastToastRef = useRef({ message: null, type: null });

  const addToast = useCallback((message, type = 'success') => {
    const id = uuidv4();
    // Determine if this is a repeated notification
    const isRepeat =
      lastToastRef.current.message === message &&
      lastToastRef.current.type === type &&
      toasts.length > 0;
    setToasts((prevToasts) => [...prevToasts, { id, message, type, shouldBounce: isRepeat }]);
    lastToastRef.current = { message, type };
    // Auto remove toast after 3 seconds
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 3000);
  }, [toasts]);

  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {toasts.map((toast) => (
        <Toast
          key={toast.id + toast.message + toast.type}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          shouldBounce={toast.shouldBounce}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
}; 